/**
 * Shared variable extraction utilities.
 * Used by both server and client code.
 */

/**
 * Extracted variables result
 */
export interface ExtractedVariables {
  /** Static variables (no @ prefix) - internal use, collapsed by default */
  static: Record<string, string>;
  /** Dynamic variables (@ prefix) - user-editable, shown prominently */
  dynamic: Record<string, string>;
}

/**
 * Variable regex pattern for matching variable definitions in frontmatter.
 * Matches: @VAR_NAME=value (dynamic) or VAR_NAME=value (static)
 */
const VARIABLE_PATTERN = /^\s*(@?)([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.+?)\s*$/;

/**
 * Frontmatter block regex.
 * Matches: <!-- vars ... -->
 */
const FRONTMATTER_PATTERN = /<!--\s*vars\s*([\s\S]*?)-->/;

/**
 * Extract variables from markdown frontmatter.
 *
 * Variables are defined in a comment block:
 * ```
 * <!-- vars
 * @HOSTNAME=example.com    <- Dynamic (user-editable)
 * @SSH_PORT=22             <- Dynamic
 * SERVER_TYPE=production   <- Static (internal)
 * -->
 * ```
 *
 * @param content - Markdown content with potential frontmatter
 * @returns Extracted static and dynamic variables
 */
export function extractVariables(content: string): ExtractedVariables {
  const staticVars: Record<string, string> = {};
  const dynamicVars: Record<string, string> = {};

  const varBlockMatch = content.match(FRONTMATTER_PATTERN);

  if (varBlockMatch) {
    const varBlock = varBlockMatch[1];
    const lines = varBlock.split('\n');

    for (const line of lines) {
      const match = line.match(VARIABLE_PATTERN);
      if (match) {
        const isDynamic = match[1] === '@';
        const varName = match[2];
        const varValue = match[3];

        if (isDynamic) {
          dynamicVars[varName] = varValue;
        } else {
          staticVars[varName] = varValue;
        }
      }
    }
  }

  return { static: staticVars, dynamic: dynamicVars };
}

/**
 * Check if content contains a frontmatter block.
 *
 * @param content - Markdown content
 * @returns true if frontmatter exists
 */
export function hasFrontmatter(content: string): boolean {
  return FRONTMATTER_PATTERN.test(content);
}

/**
 * Get all variable names from content (both static and dynamic).
 *
 * @param content - Markdown content
 * @returns Array of variable names
 */
export function getVariableNames(content: string): string[] {
  const vars = extractVariables(content);
  return [...Object.keys(vars.dynamic), ...Object.keys(vars.static)];
}

/**
 * Merge static and dynamic variables into a single record.
 * Dynamic variables take precedence over static with the same name.
 *
 * @param extracted - Extracted variables
 * @returns Merged record of all variables
 */
export function mergeVariables(extracted: ExtractedVariables): Record<string, string> {
  return {
    ...extracted.static,
    ...extracted.dynamic,
  };
}

/**
 * Update a variable value in frontmatter content.
 * Returns the updated content.
 *
 * @param content - Original markdown content
 * @param varName - Variable name to update
 * @param newValue - New value
 * @returns Updated content
 */
export function updateVariableInFrontmatter(
  content: string,
  varName: string,
  newValue: string
): string {
  const varBlockMatch = content.match(FRONTMATTER_PATTERN);
  if (!varBlockMatch) {
    return content;
  }

  const fullMatch = varBlockMatch[0];
  const varBlock = varBlockMatch[1];

  // Update the variable in the block
  const lines = varBlock.split('\n');
  const updatedLines = lines.map(line => {
    const match = line.match(VARIABLE_PATTERN);
    if (match && match[2] === varName) {
      const prefix = match[1]; // @ or empty
      return `${prefix}${varName}=${newValue}`;
    }
    return line;
  });

  const updatedBlock = updatedLines.join('\n');
  const updatedFrontmatter = fullMatch.replace(varBlock, updatedBlock);

  return content.replace(fullMatch, updatedFrontmatter);
}

/**
 * Create a frontmatter block from variables.
 *
 * @param variables - Variables to include
 * @param options - Options for formatting
 * @returns Frontmatter comment block
 */
export function createFrontmatter(
  variables: ExtractedVariables,
  options: { compact?: boolean } = {}
): string {
  const lines: string[] = [];

  // Dynamic variables first (with @ prefix)
  for (const [name, value] of Object.entries(variables.dynamic)) {
    lines.push(`@${name}=${value}`);
  }

  // Then static variables
  for (const [name, value] of Object.entries(variables.static)) {
    lines.push(`${name}=${value}`);
  }

  if (lines.length === 0) {
    return '';
  }

  if (options.compact) {
    return `<!-- vars\n${lines.join('\n')}\n-->`;
  }

  return `<!-- vars\n${lines.join('\n')}\n-->`;
}
